import {
  type AppErrorClass,
  createAppError,
  getErrorBoundaryMessage,
  logError,
} from "@/utils/errorHandler"
import { Box, Button, Heading, Text, VStack } from "@chakra-ui/react"
import { QueryErrorResetBoundary } from "@tanstack/react-query"
import React, {
  Component,
  type ErrorInfo,
  type ReactNode,
  Suspense,
} from "react"
import { FiAlertTriangle, FiRefreshCw, FiRotateCcw } from "react-icons/fi"

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: (error: AppErrorClass, retry: () => void) => ReactNode
  onError?: (error: AppErrorClass, errorInfo: ErrorInfo) => void
  level?: "page" | "feature" | "component"
  resetKeys?: Array<string | number>
  resetOnPropsChange?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error: AppErrorClass | null
  errorId: string
  retryCount: number
}

// =============================================================================
// ERROR FALLBACK COMPONENTS
// =============================================================================

export const PageErrorFallback: React.FC<{
  error: AppErrorClass
  retry: () => void
  onReload?: () => void
}> = ({ error, retry, onReload = () => window.location.reload() }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    minHeight="60vh"
    p={8}
    role="alert"
    aria-live="polite"
  >
    <VStack gap={6} textAlign="center" maxW="md">
      <Box color="red.500" fontSize="4xl" aria-hidden="true">
        <FiAlertTriangle />
      </Box>

      <VStack gap={3}>
        <Heading size="lg" color="gray.800">
          Something went wrong
        </Heading>

        <Text color="gray.600" lineHeight="tall">
          {getErrorBoundaryMessage(error)}
        </Text>

        {error.code && (
          <Text fontSize="sm" color="gray.500" fontFamily="mono">
            Error Code: {error.code}
          </Text>
        )}
      </VStack>

      <VStack gap={3}>
        <Button
          onClick={retry}
          colorScheme="blue"
          size="lg"
          aria-label="Try again"
        >
          <FiRefreshCw />
          Try Again
        </Button>

        <Button
          onClick={onReload}
          variant="ghost"
          size="sm"
          aria-label="Reload page"
        >
          <FiRotateCcw />
          Reload Page
        </Button>
      </VStack>

      {import.meta.env.DEV && (
        <Box
          mt={6}
          p={4}
          bg="gray.50"
          borderRadius="md"
          fontSize="sm"
          fontFamily="mono"
          textAlign="left"
          maxW="full"
          overflowX="auto"
          as="details"
        >
          <Text as="summary" fontWeight="bold" mb={2} cursor="pointer">
            Debug Information
          </Text>
          <Text whiteSpace="pre-wrap" fontSize="xs">
            {JSON.stringify(error.toJSON(), null, 2)}
          </Text>
        </Box>
      )}
    </VStack>
  </Box>
)

export const FeatureErrorFallback: React.FC<{
  error: AppErrorClass
  retry: () => void
}> = ({ error, retry }) => (
  <Box
    p={6}
    bg="red.50"
    border="1px"
    borderColor="red.200"
    borderRadius="md"
    role="alert"
    aria-live="polite"
  >
    <VStack gap={4} textAlign="center">
      <VStack gap={2}>
        <Box color="red.500" fontSize="2xl" aria-hidden="true">
          <FiAlertTriangle />
        </Box>
        <Heading size="md" color="red.700">
          Feature Error
        </Heading>
        <Text color="red.600" fontSize="sm">
          {error.message}
        </Text>
      </VStack>

      <Button
        onClick={retry}
        size="sm"
        colorScheme="red"
        variant="outline"
        aria-label="Retry loading this feature"
      >
        <FiRefreshCw />
        Try Again
      </Button>
    </VStack>
  </Box>
)

export const ComponentErrorFallback: React.FC<{
  error: AppErrorClass
  retry: () => void
}> = ({ error, retry }) => (
  <Box
    p={3}
    bg="orange.50"
    border="1px"
    borderColor="orange.200"
    borderRadius="md"
    fontSize="sm"
    role="alert"
    aria-live="polite"
  >
    <VStack gap={2} textAlign="center">
      <Text color="orange.700" fontWeight="medium">
        Component Error
      </Text>
      <Text color="orange.600" fontSize="xs">
        {error.message}
      </Text>
      <Button
        onClick={retry}
        size="xs"
        colorScheme="orange"
        variant="ghost"
        aria-label="Retry loading this component"
      >
        <FiRefreshCw />
        Retry
      </Button>
    </VStack>
  </Box>
)

// =============================================================================
// MAIN ERROR BOUNDARY CLASS
// =============================================================================

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private retryTimeouts: NodeJS.Timeout[] = []
  private readonly maxRetries = 3

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorId: "",
      retryCount: 0,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const appError = createAppError(error, "An unexpected error occurred")
    const errorId =
      Date.now().toString(36) + Math.random().toString(36).substr(2)

    return {
      hasError: true,
      error: appError,
      errorId,
      retryCount: 0,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const appError = createAppError(error)

    // Auto-reload on stale chunk errors (dynamic import failures after deploy)
    if (this.isChunkLoadError(error)) {
      const reloadKey = "chunk-error-reload"
      const hasReloaded = sessionStorage.getItem(reloadKey)
      if (!hasReloaded) {
        sessionStorage.setItem(reloadKey, "true")
        window.location.reload()
        return
      }
      // Already reloaded once — clear flag and show error UI
      sessionStorage.removeItem(reloadKey)
    }

    // Log the error with context
    logError(appError, `ErrorBoundary-${this.props.level || "unknown"}`)

    // Call custom error handler if provided
    this.props.onError?.(appError, errorInfo)
  }

  private isChunkLoadError(error: Error): boolean {
    const msg = error.message || ""
    return (
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk") ||
      msg.includes("Importing a module script failed")
    )
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys, resetOnPropsChange } = this.props
    const { hasError } = this.state

    // Reset error state if resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, idx) => prevProps.resetKeys?.[idx] !== key,
      )

      if (hasResetKeyChanged) {
        this.resetErrorBoundary()
      }
    }

    // Reset error state if props change and resetOnPropsChange is true
    if (
      hasError &&
      resetOnPropsChange &&
      prevProps.children !== this.props.children
    ) {
      this.resetErrorBoundary()
    }
  }

  componentWillUnmount(): void {
    this.retryTimeouts.forEach(clearTimeout)
  }

  private resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorId: "",
      retryCount: 0,
    })
  }

  private handleRetry = (): void => {
    const { retryCount } = this.state

    if (retryCount < this.maxRetries) {
      this.setState((prevState) => ({
        hasError: false,
        error: null,
        errorId: "",
        retryCount: prevState.retryCount + 1,
      }))
    } else {
      // Max retries reached, reload page
      window.location.reload()
    }
  }

  private getFallbackComponent(): React.ComponentType<{
    error: AppErrorClass
    retry: () => void
  }> {
    switch (this.props.level) {
      case "page":
        return PageErrorFallback
      case "feature":
        return FeatureErrorFallback
      case "component":
        return ComponentErrorFallback
      default:
        return PageErrorFallback
    }
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry)
      }

      // Use level-appropriate fallback
      const FallbackComponent = this.getFallbackComponent()
      return (
        <FallbackComponent error={this.state.error} retry={this.handleRetry} />
      )
    }

    return this.props.children
  }
}

// =============================================================================
// QUERY INTEGRATION BOUNDARIES
// =============================================================================

/**
 * Combines TanStack Query error reset with React Error Boundary
 */
export const QueryErrorBoundary: React.FC<{
  children: ReactNode
  level?: ErrorBoundaryProps["level"]
  fallback?: ErrorBoundaryProps["fallback"]
}> = ({ children, level = "feature", fallback }) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <ErrorBoundary
        level={level}
        fallback={fallback}
        onError={(error) => {
          // Reset query errors when component errors occur
          reset()
          logError(error, `QueryErrorBoundary-${level}`)
        }}
      >
        {children}
      </ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
)

/**
 * Suspense + Error Boundary combined for loading states
 */
export const AsyncBoundary: React.FC<{
  children: ReactNode
  level?: ErrorBoundaryProps["level"]
  fallback?: ReactNode
  errorFallback?: ErrorBoundaryProps["fallback"]
}> = ({ children, level = "feature", fallback, errorFallback }) => (
  <QueryErrorBoundary level={level} fallback={errorFallback}>
    <Suspense fallback={fallback || <div>Loading...</div>}>{children}</Suspense>
  </QueryErrorBoundary>
)

// =============================================================================
// HIGHER-ORDER COMPONENTS & HOOKS
// =============================================================================

/**
 * Higher-order component for wrapping components with error boundary
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    level?: ErrorBoundaryProps["level"]
    fallback?: ErrorBoundaryProps["fallback"]
  },
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary level={options?.level} fallback={options?.fallback}>
      <Component {...props} />
    </ErrorBoundary>
  )

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`

  return WrappedComponent
}

/**
 * Hook for throwing errors that will be caught by error boundaries
 */
export const useErrorBoundary = () => {
  const [error, setError] = React.useState<Error | null>(null)

  React.useEffect(() => {
    if (error) {
      throw error
    }
  }, [error])

  const throwError = React.useCallback((error: Error | string) => {
    const errorObj = typeof error === "string" ? new Error(error) : error
    setError(errorObj)
  }, [])

  const resetError = React.useCallback(() => {
    setError(null)
  }, [])

  return { throwError, resetError }
}

// =============================================================================
// SPECIALIZED ERROR BOUNDARIES FOR FEATURES
// =============================================================================

/**
 * Page-level error boundary with full error UI
 */
export const PageErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children,
}) => <ErrorBoundary level="page">{children}</ErrorBoundary>

/**
 * Feature-level error boundary with moderate error UI
 */
export const FeatureErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children,
}) => <QueryErrorBoundary level="feature">{children}</QueryErrorBoundary>

/**
 * Component-level error boundary with minimal error UI
 */
export const ComponentErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children,
}) => <ErrorBoundary level="component">{children}</ErrorBoundary>

// =============================================================================
// EXPORTS
// =============================================================================

export default ErrorBoundary

// Legacy compatibility exports
export { ErrorBoundary as ErrorBoundaryWrapper }
export const ErrorFallback = ComponentErrorFallback
